import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";
import { useEffect, useMemo, useState } from "react";
import { IOrganizations } from "../../../../../setup/types/Organisation";
import Spinner from "../../../../../components/common/Spinner";
import { IOrganizationBankInfo } from "../../../../../setup/api/services/organization/models";
import BankCard from "./BankCard";
import { getOrgOwnerBanks } from "../../../../../setup/api/services/payment";
import DenyAccess from "../../../../../components/denyAccess";

interface BankInformationProps {
  organization: IOrganizations | undefined;
}

const BankInformation = ({ organization }: BankInformationProps) => {
  const { t } = useTranslation();
  const [banks, setBanks] = useState<IOrganizationBankInfo[]>([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!organization) return;
    const fetchBanks = async () => {
      setIsLoading(true);
      try {
        const response = await getOrgOwnerBanks(organization.accountId);
        setBanks(response.data);
      } catch (error: any) {
        setError(error?.error?.message);
        console.error("Error fetching bank information:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBanks();
  }, [organization]);

  const defaultBank = useMemo(
    () => banks.find((bank) => bank.defaultAccount),
    [banks]
  );

  const otherBanks = useMemo(
    () => banks.filter((bank) => !bank.defaultAccount),
    [banks]
  );

  if (isLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner />
      </div>
    );
  }

  if (error === "Access is denied") {
    return <DenyAccess />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <h3 className={styles.title}>{t("lbl_bank_info_title")}</h3>
      </div>
      <div className={styles.contentContainer}>
        <div>
          {defaultBank && (
            <>
              <p className={styles.bankCardTitle}>
                {t("lbl_default_bank_account_label")}
              </p>
              <BankCard bankInfo={defaultBank} />
            </>
          )}
        </div>
        <div>
          {otherBanks.length > 0 && (
            <>
              <p className={styles.bankCardTitle}>
                {t("lbl_other_bank_accounts_label")}
              </p>
              {otherBanks.map((bank) => (
                <BankCard key={bank.id} bankInfo={bank} />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
export default BankInformation;
